"use client";

/**
 * NewForm design-system BrandMark.
 *
 * A brand's image where we have one, its initial where we don't — the square
 * that lets a row be found by shape before it is read.
 *
 * The image is assumed to be a signed storage URL, so it can fail after the
 * signature expires or the object moves. That is treated as the same case as
 * having no image at all: the initial is already the answer for "no picture",
 * and a broken-image glyph is nobody's idea of a better one.
 *
 *   import { BrandMark } from "@/components/ui/design-system/brand-mark";
 *
 *   <BrandMark
 *     name={org.name}
 *     imageUrl={org.logoUrl}
 *     className="size-7 rounded-full"
 *     fallbackClassName="bg-nf-ds-black text-[11px]"
 *   />
 *
 * Shape, size and fallback palette are the caller's, because a table row and a
 * picker row want different ones. Everything else — the fallback rule and the
 * failure handling — is here so it stays the same everywhere.
 */

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export function BrandMark({
  name,
  imageUrl,
  className,
  fallbackClassName,
}: {
  name: string;
  imageUrl: string | null;
  /** Size and shape. Applied to the image and the fallback alike, so the row
   * does not change height when one becomes the other. */
  className?: string;
  /** Background and type for the initial. */
  fallbackClassName?: string;
}) {
  const [failed, setFailed] = useState(false);

  // A new URL deserves its own attempt — re-signing is exactly how a row
  // recovers from an expired one.
  useEffect(() => setFailed(false), [imageUrl]);

  if (imageUrl && !failed) {
    return (
      // biome-ignore lint/performance/noImgElement: signed storage URL, not in the Next image allowlist
      <img
        src={imageUrl}
        alt=""
        onError={() => setFailed(true)}
        className={cn("shrink-0 object-cover", className)}
      />
    );
  }

  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center font-nf-ds-sans font-[700] leading-none text-nf-ds-white",
        className,
        fallbackClassName,
      )}
    >
      {name.trim().charAt(0).toUpperCase() || "?"}
    </span>
  );
}
